import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Input, Select } from '@components/ui';
import { useTasks } from '@hooks';
import { dayKey, localDayKey } from '@utils/calendarDate';

export default function TimeEntryModal({
  isOpen,
  onClose,
  onSubmit,
  projectId,
  initialTaskId = null,
  editData = null,
  isLoading = false,
}) {
  const [formData, setFormData] = useState({
    taskId: '',
    hours: '',
    description: '',
    date: localDayKey(),
  });

  const { data: tasksData } = useTasks({ projectId }, { enabled: !!projectId && isOpen });
  const tasks = tasksData?.data || [];

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        setFormData({
          taskId: editData.taskId?.toString() || '',
          hours: editData.hours?.toString() || '',
          description: editData.description || '',
          date: editData.date ? dayKey(editData.date) : localDayKey(),
        });
      } else {
        setFormData({
          taskId: initialTaskId?.toString() || '',
          hours: '',
          description: '',
          date: localDayKey(),
        });
      }
    }
  }, [isOpen, editData, initialTaskId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.taskId || !formData.hours || !formData.date) return;

    onSubmit({
      taskId: parseInt(formData.taskId),
      hours: parseFloat(formData.hours),
      description: formData.description,
      date: formData.date,
    });
  };

  const taskOptions = tasks.map((task) => ({
    value: task.id.toString(),
    label: `${task.title} (${task.status})`,
  }));

  const hoursOptions = [
    { value: '0.5', label: '30 minutos' },
    { value: '1', label: '1 hora' },
    { value: '1.5', label: '1.5 horas' },
    { value: '2', label: '2 horas' },
    { value: '3', label: '3 horas' },
    { value: '4', label: '4 horas' },
    { value: '5', label: '5 horas' },
    { value: '6', label: '6 horas' },
    { value: '8', label: '8 horas' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editData ? 'Editar Registro de Tiempo' : 'Registrar Tiempo'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={isLoading}
            disabled={!formData.taskId || !formData.hours || !formData.date}
          >
            {editData ? 'Actualizar' : 'Registrar'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {!initialTaskId && (
            <Select
              label="Tarea"
              name="taskId"
              value={formData.taskId}
              onChange={handleChange}
              options={taskOptions}
              placeholder="Seleccionar tarea..."
              required
            />
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Select
              label="Horas trabajadas"
              name="hours"
              value={formData.hours}
              onChange={handleChange}
              options={hoursOptions}
              placeholder="Seleccionar..."
              required
            />

            <Input
              label="Fecha"
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#475569',
                marginBottom: '6px',
              }}
            >
              Descripción (opcional)
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe lo que trabajaste..."
              rows={3}
              style={{
                width: '100%',
                padding: '10px 16px',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                fontSize: '14px',
                color: '#1e293b',
                resize: 'vertical',
                outline: 'none',
              }}
            />
          </div>

          {/* Quick time buttons */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                color: '#64748b',
                marginBottom: '8px',
              }}
            >
              Tiempo rápido
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {['0.5', '1', '2', '4', '8'].map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, hours: h }))}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: formData.hours === h ? '2px solid #667eea' : '1px solid #e2e8f0',
                    backgroundColor: formData.hours === h ? '#667eea10' : '#f8fafc',
                    color: formData.hours === h ? '#667eea' : '#64748b',
                    fontSize: '13px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {h}h
                </button>
              ))}
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
}

TimeEntryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  projectId: PropTypes.number,
  initialTaskId: PropTypes.number,
  editData: PropTypes.object,
  isLoading: PropTypes.bool,
};
